// src/pages/ToolCategoryPage.tsx
import React from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { Typography, Container, Grid, Card, CardActionArea, CardContent, Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { menuConfig } from '../config/menuConfig';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ToolCategoryPage: React.FC = () => {
    const { t } = useTranslation();
    const { category: categoryId } = useParams<{ category: string }>();
    const navigate = useNavigate();

    const currentCategory = menuConfig.find(cat => cat.id === categoryId);

    if (!currentCategory) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ position: 'relative', textAlign: 'center', mb: 4 }}>
                    <IconButton
                        onClick={() => navigate('/')}
                        aria-label="Back to Home"
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{ color: 'error.main' }}
                    >
                        Category Not Found
                    </Typography>
                </Box>
                <Typography variant="body1" component="p" sx={{ textAlign: 'center', color: 'text.secondary', mb: 4 }}>
                    The category you are looking for does not exist.
                </Typography>
            </Container>
        );
    }

    // Group tools by subcategory, including categories without subcategories
    const toolsBySubCategory = currentCategory.subCategories?.length
        ? currentCategory.subCategories.map(subCat => ({
            ...subCat,
            tools: currentCategory.tools.filter(tool => tool.subCategoryId === subCat.id),
        }))
        : [{ id: 'default', nameKey: '', descKey: '', tools: currentCategory.tools }];

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ position: 'relative', textAlign: 'center', mb: 4 }}>
                <IconButton
                    onClick={() => navigate('/')}
                    aria-label="Back to Home"
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h3" component="h1" gutterBottom>
                    {t(currentCategory.nameKey, { ns: 'tools' })}
                </Typography>
            </Box>
            <Typography variant="body1" component="p" sx={{ textAlign: 'center', color: 'text.secondary', mb: 6 }}>
                {t(currentCategory.descKey, { ns: 'tools' })}
            </Typography>

            {/* Subcategory Sections */}
            {toolsBySubCategory.map(subCategory => (
                <Box key={subCategory.id} sx={{ mb: 6 }}>
                    {/* Subcategory Header (skip for categories without subcategories) */}
                    {subCategory.nameKey && (
                        <>
                            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                {t(subCategory.nameKey, { ns: 'tools' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t(subCategory.descKey, { ns: 'tools' })}
                            </Typography>
                        </>
                    )}

                    {/* Tools Grid */}
                    <Grid container spacing={3}>
                        {subCategory.tools.map((tool) => (
                            <Grid item xs={12} sm={6} md={4} key={tool.id} {...{} as any}>
                                <Card
                                    sx={{
                                        height: '140px', // Fixed height for uniformity
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.2s',
                                        '&:hover': {
                                            transform: 'scale(1.02)',
                                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <CardActionArea
                                        component={RouterLink}
                                        to={`/tools/${currentCategory.id}/${tool.id}`}
                                        sx={{ height: '100%', display: 'flex' }}
                                    >
                                        <CardContent
                                            sx={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 2,
                                            }}
                                        >
                                            {/* Tool Icon */}
                                            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                                {tool.icon && <tool.icon sx={{ fontSize: 40, color: 'grey.700' }} />}
                                            </Box>
                                            {/* Tool Content */}
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 'medium' }}>
                                                    {t(tool.nameKey, { ns: 'tools' })}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {t(tool.descKey, { ns: 'tools' })}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Container>
    );
};

export default ToolCategoryPage;